import React, { FunctionComponent } from 'react'
import { Col, Row } from 'react-grid-system'
import { useTranslation } from 'react-i18next'

import {
  StyledEnrolledSuccessCard,
  StyledEnrolledSuccessCardAnnounceBar,
  StyledEnrolledSuccessPhysicianDetails,
  StyledSuccessActions,
  StylesEnrolledSuccessCardIcon
} from './../EnrolUserOnToProgram.styles'
import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinStepper } from '../../../components/ClinStepper'
import { StepState } from '../../../components/ClinStepper/ClinStepper'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import {
  NewPhysicianEnrollDto,
  PhysiciansSummaryDto,
  ProgramCatalogDto
} from '../../../types/swaggerTypes'
import {
  StyledSummaryHeading,
  StyledSummaryIcon
} from '../../CheckoutSummary/CheckoutSummary.styles'

interface IEnrolUserOnToProgramSuccessProps {
  /** Selected program */
  program?: ProgramCatalogDto
  /** Call back request to view program */
  handleRequestToViewProgram: () => void
  /** Call back request to view dashboard */
  handleRequestToViewDashboard: () => void
  handleRequestToViewPatientDashboard: () => void
  fromPatientDashboard?: boolean
  selectedPhysicians?: PhysiciansSummaryDto[] | undefined
  savedPhysicians?: NewPhysicianEnrollDto[] | undefined
}

export const EnrolUserOnToProgramSuccess: FunctionComponent<
  IEnrolUserOnToProgramSuccessProps
> = ({
  program,
  handleRequestToViewProgram,
  handleRequestToViewDashboard,
  handleRequestToViewPatientDashboard,
  selectedPhysicians,
  savedPhysicians,
  fromPatientDashboard
}) => {
  const { t } = useTranslation()

  return (
    <ClinPageContentFrame>
      <Row>
        <Col sm={12} md={9}>
          <ClinText
            as="h1"
            className="physician-full-name"
            variant={TypographyVariant.H2}
            fontWeight={ClinTheme.fontWeights.bold}
          >
            {t('enrol_user_on_to_program:title', {
              programName: program?.program?.programName
            })}
          </ClinText>
          <ClinSpacer height={ClinTheme.space[5]} />

          <ClinStepper
            steps={[
              {
                state: StepState.Complete,
                displayText: t('enrol_user_on_to_program:stepper.step_1')
              },
              {
                state: StepState.Complete,
                displayText: t('enrol_user_on_to_program:stepper.step_2')
              },
              {
                state: StepState.Complete,
                displayText: t('enrol_user_on_to_program:stepper.step_3')
              }
            ]}
            width={600}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={9}>
          <ClinSpacer height={ClinTheme.space[8]} hasBorder />
        </Col>
      </Row>
      <StyledSummaryHeading>
        <StyledSummaryIcon>
          <ClinIcon
            iconName={ClinIconPathName.Tick}
            iconSize={ClinTheme.space[4]}
          />
        </StyledSummaryIcon>
        <ClinText
          variant={TypographyVariant.H4}
          as="span"
          fontWeight={ClinTheme.fontWeights.bold}
          marginLeft={ClinTheme.space[3]}
        >
          {t('enrol_user_on_to_program:submitted_title')}
        </ClinText>
      </StyledSummaryHeading>
      <ClinSpacer height={ClinTheme.space[5]} />
      <Row>
        <Col sm={12} md={12} lg={8}>
          <ClinText
            as="p"
            variant={TypographyVariant.LargeParagraph}
            marginBottom={ClinTheme.space[4]}
          >
            {t('enrol_user_on_to_program:confirmation_copy_1')}
          </ClinText>

          {selectedPhysicians && selectedPhysicians.length > 0 && (
            <StyledEnrolledSuccessCard>
              <ClinText
                as="h3"
                fontWeight={ClinTheme.fontWeights.bold}
                fontSize={ClinTheme.fontSizes[3]}
                marginBottom={ClinTheme.space[4]}
              >
                {t('enrol_user_on_to_program:existing_physicians_title')}
              </ClinText>

              <StyledEnrolledSuccessCardAnnounceBar>
                <StylesEnrolledSuccessCardIcon>
                  <ClinIcon
                    iconName={ClinIconPathName.Tick}
                    iconSize={ClinTheme.space[4]}
                  />
                </StylesEnrolledSuccessCardIcon>
                <article>
                  <ClinText
                    as={'h5'}
                    color={ClinTheme.colors.darkPurple}
                    variant={TypographyVariant.LargeParagraph}
                    fontWeight={ClinTheme.fontWeights.bold}
                    marginBottom={ClinTheme.space[2]}
                    wordBreak="break-all"
                  >
                    {t(
                      'enrol_user_on_to_program:verification_eligibility_text'
                    )}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    {t('enrol_user_on_to_program:confirmation_sub_copy')}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    - {t('enrol_user_on_to_program:confirmation_bullet_1')}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    - {t('enrol_user_on_to_program:confirmation_bullet_2')}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    - {t('enrol_user_on_to_program:confirmation_bullet_3')}
                  </ClinText>

                  <ClinText
                    as="div"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    marginTop={ClinTheme.space[2]}
                    wordBreak="break-all"
                  >
                    {t('enrol_user_on_to_program:confirmation_sub')}
                  </ClinText>
                </article>
              </StyledEnrolledSuccessCardAnnounceBar>

              <ClinSpacer height={ClinTheme.space[3]} />
              {selectedPhysicians?.map((physician, index) => (
                <StyledEnrolledSuccessPhysicianDetails key={index}>
                  <ClinText
                    as="p"
                    fontWeight={ClinTheme.fontWeights.bold}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    {physician.physicianFullName}
                  </ClinText>
                  <ClinText
                    as="p"
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    {physician.physicianEmail}
                  </ClinText>
                </StyledEnrolledSuccessPhysicianDetails>
              ))}
            </StyledEnrolledSuccessCard>
          )}

          {savedPhysicians && savedPhysicians.length > 0 && (
            <StyledEnrolledSuccessCard>
              <ClinText
                as="h4"
                fontWeight={ClinTheme.fontWeights.bold}
                fontSize={ClinTheme.fontSizes[3]}
                marginBottom={ClinTheme.space[4]}
                wordBreak="break-all"
              >
                {t('enrol_user_on_to_program:new_physicians_title')}
              </ClinText>
              <StyledEnrolledSuccessCardAnnounceBar>
                <StylesEnrolledSuccessCardIcon>
                  <ClinIcon
                    iconName={ClinIconPathName.Tick}
                    iconSize={ClinTheme.space[4]}
                  />
                </StylesEnrolledSuccessCardIcon>
                <article>
                  <ClinText
                    as={'h5'}
                    color={ClinTheme.colors.darkPurple}
                    fontWeight={ClinTheme.fontWeights.bold}
                    fontSize={ClinTheme.fontSizes[2]}
                    marginBottom={ClinTheme.space[2]}
                    wordBreak="break-all"
                  >
                    {t(
                      'enrol_user_on_to_program:verification_eligibility_text'
                    )}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    {t('enrol_user_on_to_program:confirmation_sub_copy')}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    - {t('enrol_user_on_to_program:confirmation_bullet_1')}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    - {t('enrol_user_on_to_program:confirmation_bullet_2')}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    - {t('enrol_user_on_to_program:confirmation_bullet_3')}
                  </ClinText>

                  <ClinText
                    as="p"
                    color={ClinTheme.colors.darkPurple4}
                    marginTop={ClinTheme.space[2]}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    {t('enrol_user_on_to_program:confirmation_sub')}
                  </ClinText>
                </article>
              </StyledEnrolledSuccessCardAnnounceBar>

              <ClinSpacer height={ClinTheme.space[4]} />
              {savedPhysicians?.map((physician, index) => (
                <StyledEnrolledSuccessPhysicianDetails key={index}>
                  <ClinText
                    as="div"
                    fontWeight={ClinTheme.fontWeights.bold}
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    {physician.title} {physician.firstName} {physician.lastName}
                  </ClinText>
                  <ClinText
                    as="div"
                    variant={TypographyVariant.LargeParagraph}
                    wordBreak="break-all"
                  >
                    {physician.emailAddress}
                  </ClinText>
                </StyledEnrolledSuccessPhysicianDetails>
              ))}
            </StyledEnrolledSuccessCard>
          )}

          <ClinSpacer height={ClinTheme.space[7]} />
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={10} lg={8}>
          {!fromPatientDashboard ? (
            <StyledSuccessActions>
              <ClinButton
                onClick={() => handleRequestToViewProgram()}
                variant="secondary"
              >
                {t('enrol_user_on_to_program:view_program_button')}
              </ClinButton>
              <ClinButton
                onClick={() => handleRequestToViewDashboard()}
                variant="secondary"
              >
                {t('enrol_user_on_to_program:view_dashboard_button')}
              </ClinButton>
            </StyledSuccessActions>
          ) : (
            <StyledSuccessActions>
              <ClinButton
                onClick={() => handleRequestToViewProgram()}
                variant="purpleSecondaryNarrow"
              >
                {t('enrol_user_on_to_program:view_program_button')}
              </ClinButton>
              <ClinButton
                style={{
                  backgroundColor: ClinTheme.colors.primaryLight, //TODO - change this to variant='purpleLightNarrow' after release (we have this button changes on develop branch)
                  borderColor: ClinTheme.colors.primaryLight
                }}
                onClick={() => handleRequestToViewPatientDashboard()}
                variant="purpleNarrow"
              >
                {t('enrol_user_on_to_program:view_patient_dashboard_button')}
              </ClinButton>
            </StyledSuccessActions>
          )}
        </Col>
      </Row>
      <ClinSpacer height={ClinTheme.space[8]} />
    </ClinPageContentFrame>
  )
}
